type Win = {
  document: Pick<Document, "addEventListener"> & { activeElement: { tagName: string } | null };
  scrollTo: (x: number, y: number) => void;
  setTimeout: (fn: () => void, ms?: number) => unknown;
  visualViewport?: Pick<EventTarget, "addEventListener"> | null;
};

const TYPING = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/**
 * App feel in a browser: the page itself can't be pinch-zoomed. iOS Safari ignores
 * `user-scalable=no`, hence its gesture events and two-finger moves are cancelled. The map still
 * zooms: it reads the same touches, only the browser's default zoom is stopped. Double-tap zoom:
 * `touch-action` in CSS.
 *
 * Keyboard: screens are pinned to the viewport's edges (base.css), nothing is measured, so nothing
 * stays shrunk once the keyboard closes. iOS may still leave the page scrolled up to where the field
 * was: back to the top when the focus leaves the fields (and doesn't move to another one), and
 * whenever the visible area changes with no field focused — the keyboard closed. The latter also
 * covers a field removed while focused (a code that validates itself and changes screen), for which
 * iOS sends no focusout.
 */
export function installViewport(win: Win = window) {
  const cancel = (e: Event) => e.preventDefault();
  for (const type of ["gesturestart", "gesturechange", "gestureend"])
    win.document.addEventListener(type, cancel);
  win.document.addEventListener(
    "touchmove",
    (e: TouchEvent) => {
      if (e.touches.length > 1) e.preventDefault();
    },
    { passive: false },
  );
  const settle = () =>
    win.setTimeout(() => {
      const el = win.document.activeElement;
      if (!el || !TYPING.has(el.tagName)) win.scrollTo(0, 0);
    }, 0);
  win.document.addEventListener("focusout", settle);
  win.visualViewport?.addEventListener("resize", settle);
}
